import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Link } from "@inertiajs/react";
import {Edit, IdCard, PhoneCall} from "lucide-react";
import {Patient, PatientWithOwesMoney} from "@/lib/types/models.ts";

interface Props {
    patients: PatientWithOwesMoney[];
    translations: any;
    onEdit: (patient: Patient) => void;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    onSortChange?: (column: string) => void;
    loading?: boolean;
}

export default function PatientsTable({
                                          patients,
                                          translations: t,
                                          onEdit,
                                          sortBy,
                                          sortDir,
                                          onSortChange,
                                          loading
                                      }: Props) {
    const headers = [
        { key: "name", label: t.table_name },
        { key: "record_number", label: t.table_record_number },
        { key: "call_before_appointment", label: t.table_call_before },
        { key: "phone_number", label: t.table_phone },
        { key: "city", label: t.table_city },
        { key: "year_of_birth", label: t.table_year_of_birth },
        { key: "doctor", label: t.table_doctor },
        { key: "orthodontics", label: t.table_orthodontics },
        { key: "orthodontist", label: t.table_orthodontist },
        { key: "owes_money", label: t.table_owes_money },
    ];

    const handleSort = (key: string) => {
        if (!onSortChange) return;
        onSortChange(key);
    };

    return (
        <div className="relative">
            <Table>
                <TableHeader>
                    <TableRow>
                        {headers.map(({ key, label }) => (
                            <TableHead
                                key={key}
                                className={`cursor-pointer select-none ${
                                    sortBy === key ? "font-semibold" : ""
                                }`}
                                onClick={() => handleSort(key)}
                            >
                                {label}
                                {sortBy === key && (
                                    <span>{sortDir === "asc" ? " ▲" : " ▼"}</span>
                                )}
                            </TableHead>
                        ))}
                        <TableHead>{t.table_card}</TableHead>
                        <TableHead>{t.table_actions}</TableHead>
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {patients.map((patient) => (
                        <TableRow key={patient.id} className="hover:bg-muted/50">
                            <TableCell className="font-medium">{patient.name}</TableCell>
                            <TableCell>
                                {patient.patient_card?.record_number ?? "-"}
                            </TableCell>
                            <TableCell className="flex justify-center items-center">
                                {patient.call_before_appointment ? (
                                    <PhoneCall className="w-4 h-4" />
                                ) : (
                                    <span className="text-sm text-muted-foreground">-</span>
                                )}
                            </TableCell>
                            <TableCell>{patient.phone_number ?? "-"}</TableCell>
                            <TableCell>{patient.city ?? "-"}</TableCell>
                            <TableCell>{patient.year_of_birth ?? "-"}</TableCell>
                            <TableCell className="flex items-center gap-2">
                                {patient.doctor ? (
                                    <>
                                        <span
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: patient.doctor.color ?? "#ddd" }}
                                            aria-hidden
                                        />
                                        <span>{patient.doctor.name}</span>
                                    </>
                                ) : (
                                    <span className="text-sm text-muted-foreground">-</span>
                                )}
                            </TableCell>
                            <TableCell className="text-center">
                                {patient.orthodontics ? (
                                    <span className="font-medium">Da</span>
                                ) : (
                                    <span className="text-sm text-muted-foreground">Ne</span>
                                )}
                            </TableCell>
                            <TableCell>
                                {patient.orthodontics && patient.orthodontist ? (
                                    <span>{patient.orthodontist.name}</span>
                                ) : (
                                    <span className="text-sm text-muted-foreground">-</span>
                                )}
                            </TableCell>
                            <TableCell className="text-center">
                                {patient.owes_money ? (
                                    <span className="font-medium" >Da</span>
                                ) : (
                                    <span className="text-sm text-muted-foreground">Ne</span>
                                )}
                            </TableCell>
                            <TableCell>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    asChild
                                    className="hover:text-green-600"
                                >
                                    <Link href={`/patient_cards/${patient.patient_card!.id}`} target="_blank">
                                        <IdCard />
                                    </Link>
                                </Button>
                            </TableCell>
                            <TableCell className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => onEdit(patient)}
                                >
                                    <Edit/>
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {loading && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-md">
                    <span className="text-gray-700">{t.loading}</span>
                </div>
            )}
        </div>
    );
}
